import { useState } from 'react';
import { api } from '../api/client';
import type { Municipality, RestrictionLevel, WaterRestriction, WaterRestrictionPricedRun, WaterRestrictionRefusal } from '../api/types';
import { Alert, Button } from './ui';
import { dayLabel, keyToMonth, LEVEL_LABELS, monthToKey, RESTRICTION_LEVELS, stepDown } from '../lib/waterRestrictions';

// One municipality's water restriction entries (spec 2026-10-08): a dated
// list, add/edit/delete, the "then from" step-down prompt, and the amber
// priced-runs warning. billing:admin only: rendered inside
// MunicipalityManager, which is hidden from everyone else.

interface FormState {
  id: string | null;            // null = a new entry
  level: RestrictionLevel;
  from: string;                 // <input type="month"> value, 'YYYY-MM'
  to: string;                   // '' = open-ended
}

interface Props {
  municipality: Municipality;
  /** The parent reloads municipalities (and so these entries) after a change. */
  onChanged: () => void;
}

const STATUS_LABELS: Record<WaterRestrictionPricedRun['status'], string> = {
  approved: 'approved', finalized: 'finalized', pending_approval: 'pending approval', calculating: 'calculating',
};

const runLine = (r: WaterRestrictionPricedRun) =>
  `${r.pocket_name ?? r.realm}: ${dayLabel(r.period_start)} to ${dayLabel(r.period_end)} (${STATUS_LABELS[r.status]})`;

const rangeLabel = (e: Pick<WaterRestriction, 'effective_from' | 'effective_to'>) =>
  e.effective_to ? `${dayLabel(e.effective_from)} to ${dayLabel(e.effective_to)}` : `from ${dayLabel(e.effective_from)}, open-ended`;

const entryName = (e: WaterRestriction) => `${LEVEL_LABELS[e.level]} from ${dayLabel(e.effective_from)}`;

export function WaterRestrictions({ municipality, onChanged }: Props) {
  const entries = municipality.water_restrictions ?? [];
  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [priced, setPriced] = useState<{ message: string; runs: WaterRestrictionPricedRun[]; action: 'save' | 'delete'; retry: () => void } | null>(null);
  const [stepPrompt, setStepPrompt] = useState<{ from: string; level: RestrictionLevel } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  // Calculated, unapproved runs the change did not reach (Q7): a note, never a block.
  const [toRerun, setToRerun] = useState<WaterRestrictionPricedRun[]>([]);

  const refused = (err: unknown, action: 'save' | 'delete', retry: () => void) => {
    const status = (err as { status?: number }).status;
    const body = (err as { body?: WaterRestrictionRefusal }).body;
    if (status === 409 && body?.code === 'restriction_priced_runs') {
      setPriced({ message: body.error, runs: body.runs ?? [], action, retry });
    } else {
      setError(err instanceof Error ? err.message : 'The restriction could not be saved');
    }
  };

  const save = async (f: FormState, acknowledge: boolean) => {
    const body = {
      level: f.level,
      effective_from: monthToKey(f.from),
      effective_to: f.to ? monthToKey(f.to) : null,
      ...(acknowledge ? { acknowledge_priced_runs: true } : {}),
    };
    setBusy(true);
    setError(null);
    setPriced(null);
    setToRerun([]);
    try {
      const res = f.id
        ? await api.updateWaterRestriction(municipality.id, f.id, body)
        : await api.createWaterRestriction(municipality.id, body);
      setToRerun(res.pending_runs ?? []);
      setForm(null);
      setStepPrompt(body.effective_to ? { from: body.effective_to, level: f.level } : null);
      onChanged();
    } catch (err) {
      refused(err, 'save', () => { void save(f, true); });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string, acknowledge: boolean) => {
    setBusy(true);
    setError(null);
    setPriced(null);
    setToRerun([]);
    try {
      const res = await api.deleteWaterRestriction(municipality.id, id, acknowledge);
      setToRerun(res.pending_runs ?? []);
      setPendingDelete(null);
      onChanged();
    } catch (err) {
      refused(err, 'delete', () => { void remove(id, true); });
    } finally {
      setBusy(false);
    }
  };

  const openForm = (f: FormState) => {
    setError(null);
    setPriced(null);
    setStepPrompt(null);
    setForm(f);
  };

  const milder = stepPrompt ? stepDown(stepPrompt.level) : null;

  return (
    <section aria-label={`Water restrictions for ${municipality.name}`} className="mt-2 space-y-2 rounded-lg bg-surface-sunken p-3">
      {error && <Alert tone="danger"><p>{error}</p></Alert>}

      {priced && (
        <div role="alert">
          <Alert tone="warning">
            <p>{priced.message}</p>
            {priced.runs.length > 0 && (
              <ul className="mt-2 list-disc pl-5 text-sm space-y-0.5">
                {priced.runs.map((r) => <li key={r.id}>{runLine(r)}</li>)}
              </ul>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" onClick={priced.retry} disabled={busy}>Acknowledge and {priced.action}</Button>
              <Button size="sm" variant="secondary" onClick={() => setPriced(null)}>Cancel</Button>
            </div>
          </Alert>
        </div>
      )}

      {toRerun.length > 0 && (
        <Alert tone="info">
          <p>These runs were calculated before this change. Re-run these to pick up the change:</p>
          <ul className="mt-2 list-disc pl-5 text-sm space-y-0.5">
            {toRerun.map((r) => <li key={r.id}>{runLine(r)}</li>)}
          </ul>
        </Alert>
      )}

      {entries.length === 0 ? (
        <p className="text-sm text-content-muted">No restrictions: Water Wise.</p>
      ) : (
        <ul className="divide-y divide-border-token">
          {entries.map((e) => (
            <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
              <span className="min-w-0 text-sm text-content">
                <span className="font-medium">{LEVEL_LABELS[e.level]}</span>{' '}
                <span className="text-content-muted">{rangeLabel(e)}</span>
              </span>
              {pendingDelete === e.id ? (
                <span className="flex shrink-0 items-center gap-2">
                  <Button variant="danger" size="sm" onClick={() => remove(e.id, false)} disabled={busy}>Confirm delete</Button>
                  <Button variant="secondary" size="sm" onClick={() => setPendingDelete(null)}>Cancel</Button>
                </span>
              ) : (
                <span className="flex shrink-0 items-center gap-1">
                  <Button variant="ghost" size="sm" aria-label={`Edit ${entryName(e)}`}
                    onClick={() => openForm({ id: e.id, level: e.level, from: keyToMonth(e.effective_from), to: e.effective_to ? keyToMonth(e.effective_to) : '' })}>
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" aria-label={`Delete ${entryName(e)}`} onClick={() => setPendingDelete(e.id)}>
                    Delete
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {stepPrompt && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-content">
          <span>Then from {dayLabel(stepPrompt.from)}:</span>
          {/* No entry is Water Wise, so that choice adds nothing (Decision 4). */}
          <Button size="sm" variant="secondary" onClick={() => setStepPrompt(null)}>Water Wise</Button>
          {milder && (
            <Button size="sm" variant="secondary"
              onClick={() => openForm({ id: null, level: milder, from: keyToMonth(stepPrompt.from), to: '' })}>
              {LEVEL_LABELS[milder]}
            </Button>
          )}
        </div>
      )}

      {form ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(ev) => { ev.preventDefault(); void save(form, false); }}
        >
          <div className="min-w-[8rem] flex-1">
            <label htmlFor={`wr-level-${municipality.id}`} className="block text-xs font-medium text-content mb-1">Level</label>
            <select id={`wr-level-${municipality.id}`} value={form.level}
              onChange={(ev) => setForm({ ...form, level: ev.target.value as RestrictionLevel })}
              className="w-full px-3 py-2 border border-border-token rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent">
              {RESTRICTION_LEVELS.map((l) => <option key={l} value={l}>{LEVEL_LABELS[l]}</option>)}
            </select>
          </div>
          <div className="min-w-[8rem] flex-1">
            <label htmlFor={`wr-from-${municipality.id}`} className="block text-xs font-medium text-content mb-1">From</label>
            <input id={`wr-from-${municipality.id}`} type="month" required value={form.from}
              onChange={(ev) => setForm({ ...form, from: ev.target.value })}
              className="w-full px-3 py-2 border border-border-token rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
          </div>
          <div className="min-w-[8rem] flex-1">
            <label htmlFor={`wr-to-${municipality.id}`} className="block text-xs font-medium text-content mb-1">To (optional)</label>
            <input id={`wr-to-${municipality.id}`} type="month" value={form.to}
              aria-describedby={`wr-to-hint-${municipality.id}`}
              onChange={(ev) => setForm({ ...form, to: ev.target.value })}
              className="w-full px-3 py-2 border border-border-token rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
          </div>
          <p id={`wr-to-hint-${municipality.id}`} className="w-full text-xs text-content-muted">
            Both are the 1st of the month. The end is the month it stops: 1 Nov to 1 Dec covers November. Leave it empty for open-ended.
          </p>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={busy || !form.from}>Save restriction</Button>
            <Button type="button" size="sm" variant="secondary" onClick={() => setForm(null)}>Cancel</Button>
          </div>
        </form>
      ) : (
        <Button size="sm" variant="secondary"
          onClick={() => openForm({ id: null, level: 'level_1', from: '', to: '' })}>
          Add restriction
        </Button>
      )}
    </section>
  );
}
